<script lang="ts" setup>
import { costs } from '@thecompaniesapi/shared/client'
import { z } from 'zod'

const props = defineProps({
  domains: {
    default: undefined,
    type: Array as PropType<string[]>,
  },
  feature: {
    required: true,
    type: String as PropType<SegmentationFeature>,
  },
  list: {
    default: undefined,
    type: Object as PropType<List>,
  },
  query: {
    default: undefined,
    type: Array as PropType<SegmentationCondition[]>,
  },
})

const emit = defineEmits<{
  close: []
}>()

const { createEvent } = useAnalytics()
const { createToast } = useToasts()
const { t } = useI18n()
const listsStore = useListsStore()
const localePath = useLocalePath()
const router = useRouter()
const { slideovers } = useOverlays()
const teamsStore = useTeamsStore()
const savingCompanies = ref(false)

const form = reactive({
  listMaxCompanies: { maxCompanies: 100 },
  listSelector: { listId: undefined },
})

watch(
  () => form.listMaxCompanies.maxCompanies,
  (value) => {
    if (value < 1) {
      form.listMaxCompanies.maxCompanies = 1
    }
    else if (value > (slideovers?.listSaver?.data?.queryCount || 0)) {
      form.listMaxCompanies.maxCompanies = slideovers?.listSaver?.data?.queryCount || 0
    }
  },
)

const canSaveCompaniesCount = computed(() => {
  const companyCost = costs.globals.company
  const credits = teamsStore.currentTeam?.credits || 0

  return Math.min(
    Math.floor(credits / companyCost),
    (slideovers?.listSaver?.data?.queryCount || 0) > form.listMaxCompanies.maxCompanies
      ? form.listMaxCompanies.maxCompanies
      : slideovers?.listSaver?.data?.queryCount || 0,
  )
})

const validation = { listMaxCompanies: z.object({ maxCompanies: z.number().min(1, { message: t('validation.required') }) }) }

function setForm() {
  // Adapt form to list
  if (props.list) {
    form.listMaxCompanies.maxCompanies = props.list.maxCompanies || 100
  }
  else {
    const credits = teamsStore.currentTeam?.credits || 0

    form.listMaxCompanies.maxCompanies = credits < (slideovers?.listSaver?.data?.queryCount || 0)
      ? credits
      : (slideovers?.listSaver?.data?.queryCount || 0)
  }
}

async function onSaveCompanies() {
  try {
    if (!savingCompanies.value) {
      savingCompanies.value = true

      // Save companies from query
      if (props.feature === 'search') {
        await listsStore.toggleCompaniesFromList({
          listId: form.listSelector.listId,
          maxCompanies: form.listMaxCompanies.maxCompanies,
          onToggleCompanies: slideovers?.listSaver?.data?.onToggleCompanies,
          query: props.query,
        })
      }
      // Save companies from similar domains
      else if (props.feature === 'similar') {
        await listsStore.toggleCompaniesFromList({
          domains: props.domains,
          listId: form.listSelector.listId,
          onToggleCompanies: slideovers?.listSaver?.data?.onToggleCompanies,
        })
      }

      createEvent({
        name: '[Lists] Create List',
        properties: { Feature: props.feature },
      })

      createToast({ message: 'listAddingCompanies' })

      router.push(
        localePath({
          name: 'companies-lists-listId',
          params: { listId: `${form.listSelector.listId}` },
          query: { 'wait-companies': 'true' },
        }),
      )

      savingCompanies.value = false

      emit('close')
    }
  }
  catch (error) {
    console.error(error)

    savingCompanies.value = false
  }
}

setForm()
</script>

<template>
  <UDashboardSlideover :title="t('title', { count: canSaveCompaniesCount })">
    <UForm
      key="listMaxCompanies"
      class="flex flex-col gap-4"
      :schema="validation.listMaxCompanies"
      :state="form.listMaxCompanies"
      :validate-on="['submit']"
      @submit="onSaveCompanies"
    >
      <UCard>
        <template #header>
          <BaseQuote
            size="xs"
            :text="t('listMaxCompanies.quote', { companiesCount: numbro(slideovers?.listSaver?.data?.queryCount || 0) })"
          />
        </template>

        <div class="grid grid-cols-1 gap-6">
          <UFormGroup name="maxCompanies">
            <UInput
              v-model="form.listMaxCompanies.maxCompanies"
              :min="1"
              type="number"
              :ui="{ color: { white: { outline: 'focus:ring-inset' } } }"
            />
          </UFormGroup>

          <div class="flex items-center gap-1">
            <BaseCoin />

            <span class="text-xs font-medium uppercase tracking-tighter">
              {{ t('listMaxCompanies.creditsCost', { costPerCompany: costs.globals.company, credits: numbro(form?.listMaxCompanies?.maxCompanies || 0) }) }}
            </span>
          </div>
        </div>
      </UCard>

      <UCard>
        <template #header>
          <div class="flex items-center gap-2">
            <span>
              {{ t('listSelector') }}
            </span>
          </div>
        </template>

        <ListSelector
          v-model="form.listSelector.listId"
          :query="query"
        />
      </UCard>

      <UTooltip :text="!form.listSelector.listId ? t('tooltips.selectList') : canSaveCompaniesCount > 0 ? '' : t('tooltips.noCompanies')">
        <UButton
          block
          :disabled="!canSaveCompaniesCount || !form.listSelector.listId"
          :icon="getIcon('save')"
          :label="t('title', { count: canSaveCompaniesCount })"
          size="xl"
          type="submit"
          :ui="{ icon: { base: 'text-green-600 dark:text-green-400' } }"
        />
      </UTooltip>
    </UForm>
  </UDashboardSlideover>
</template>

<i18n lang="json">
{
  "en": {
    "listMaxCompanies": {
      "creditsCost": "Cost: {credits} credits ({costPerCompany} crédit per company)",
      "quote": "We identified {companiesCount} companies matching your query. How many companies do you want to save?"
    },
    "listSelector": "Select a list to save companies in",
    "title": "Save company in the list | Save {count} companies in the list",
    "tooltips": {
      "noCompanies": "No companies can be saved.",
      "selectList": "You need to select a list."
    }
  },
  "es": {
    "listMaxCompanies": {
      "creditsCost": "Coste: {credits} créditos ({costPerCompany} crédito por empresa)",
      "quote": "Hemos identificado {companiesCount} empresas que coinciden con tu consulta. ¿Cuántas empresas quieres guardar?"
    },
    "listSelector": "Selecciona una lista para guardar las empresas",
    "title": "Guardar empresa en la lista | Guardar {count} empresas en la lista",
    "tooltips": {
      "noCompanies": "No se pueden guardar empresas.",
      "selectList": "Necesitas seleccionar una lista."
    }
  },
  "fr": {
    "listMaxCompanies": {
      "creditsCost": "Coût : {credits} crédits ({costPerCompany} crédit par entreprise)",
      "quote": "Nous avons identifié {companiesCount} entreprises correspondant à votre requête. Combien d'entreprises désirez-vous sauvegarder ?"
    },
    "listSelector": "Sélectionnez une liste pour enregistrer les entreprises",
    "title": "Enregistrer une entreprise dans la liste | Enregistrer {count} entreprises dans une liste",
    "tooltips": {
      "noCompanies": "Aucune entreprise ne peut être sauvegardée.",
      "selectList": "Vous devez sélectionner une liste."
    }
  }
}
</i18n>
